<template>
  <div
    class="phone-input"
    @input.capture="onCapture"
  >
    <div class="input left flex-row align-items-center has-background-white">
      <span :class="{'flag': isChrome, 'flag-default': !isChrome}">{{ countryStore.flag }}</span>
    </div>
    <span class="input">+{{ countryStore.phoneCountryCode }}</span>
    <InputText
      class="input right"
      type="tel"
      autocomplete="tel-national"
      v-model="phone"
      @update:modelValue="onInput"
      @keydown.enter="emitSend"
      @blur="onBlur"
    />
  </div>
</template>

<script setup>
import { useCountryStore } from "~~/stores/country";
import {
  formatPhoneInput,
  isForeignPhone,
  normalizePhoneInput,
  toFullPhone,
  toLocalPhone,
} from "~~/utils/phone";

const countryStore = useCountryStore();

const props = defineProps(["modelValue"]);
const emit = defineEmits(["update:modelValue", "send", "foreign"]);

const phone = ref(null);

// whether the whole value was put in at once - autofill (no input type, or a replacement
// text), paste or drop - rather than typed in or deleted from
function isPutInAtOnce(event) {
  return (
    !event.inputType ||
    /^insert(From(Paste|Drop|Yank)|ReplacementText)/.test(event.inputType)
  );
}

// the field is not masked with the phone format, because the numbers of a country are not
// all the same length (the austrian ones vary) and the mask would cut them off - the
// number is grouped by the format instead, which lets it run past the format.
// A number put in at once is written the way it was saved (+36 30 123 4567,
// 06 30 123 4567), so it is cut down to the local number, but what is typed is left as it
// is: a trunk prefix (06) would be cut off as soon as it is typed. The emitted number is
// normalized either way, and a number of another country is always refused.
// The number is grouped while it is typed at its end, an edit in the middle of it is only
// grouped when the field is left, so that the caret does not jump away from the edit.
function onCapture(event) {
  const input = event.target;
  const putInAtOnce = isPutInAtOnce(event);

  if (
    (putInAtOnce || isForeignPhone(input.value)) &&
    !normalizePhoneInput(event)
  ) {
    phone.value = "";
    emit("update:modelValue", "");
    emit("foreign");
    return;
  }

  if (putInAtOnce || input.selectionStart === input.value.length) {
    input.value = formatPhoneInput(input.value);
  }
}

function onBlur() {
  phone.value = formatPhoneInput(phone.value);
}

// const isChrome = /Chrome/.test(navigator.userAgent);
const isChrome = true;

function emitSend(event) {
  emit("send", event);
}

onBeforeMount(() => {
  phone.value = formatPhoneInput(toLocalPhone(props.modelValue));
});

const onInput = () => {
  emit("update:modelValue", toFullPhone(phone.value));
};
</script>

<style scoped lang="scss">
.phone-input {
  display: flex;
  flex-direction: row;

  .input {
    padding: 14px 0.25em 14px 8px;
    border: none;
    border-top: 1px solid var(--gray400);
    border-bottom: 1px solid var(--gray400);
    line-height: 25px;

    &.left {
      position: relative;
      border-radius: 8px 0 0 8px;
      border-left: 1px solid var(--gray400);
      padding: 14px 8px 14px 8px;
      font-size: 8px;

      &::after {
        content: "";
        position: absolute;
        right: 0;
        width: 1px;
        height: 32px;
        background: var(--gray400);
      }
    }

    &.right {
      border-radius: 0 8px 8px 0;
      border-right: 1px solid var(--gray400);
      padding: 14px 14px 14px 0;
      flex: 1;
    }
  }
}
</style>

<style lang="scss">
.phone-input .input.left {
  align-items: center;
}
</style>
